// src/components/form-builder/FormArea.jsx
import { Box, Typography, Grid, TextField, FormControl, InputLabel, Select, MenuItem, IconButton, Button } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import FormField from './FormField';
import {
  Delete as DeleteIcon,
  Close as CloseIcon,
  Add as AddIcon
} from '@mui/icons-material';

const FormArea = ({ 
  formFields = [], 
  onRemoveField = () => {}, 
  onUpdateField = () => {}, 
  onReorderFields = () => {}, 
  onAddField = () => {}, 
  onDuplicateField = () => {}, 
  onAddTableColumn = () => {},
  onUpdateTableColumn = () => {},
  onRemoveTableColumn = () => {},
  onAddSelectOption = () => {},
  onUpdateSelectOption = () => {},
  onRemoveSelectOption = () => {},
  calculationValues = {},
  onCalculationChange = () => {},
  backgroundPdf = null, // Arka plan PDF'i
  showPdfBackground = false, // PDF arka plan gösterimi
  onFieldPositionUpdate = () => {} // Alan pozisyon güncellemesi
}) => {
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(formFields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    onReorderFields(items);
  };

  const handleFieldClick = (field) => {
    console.log(`Field clicked: ${field.id}`);
    onAddField(field);
  };
  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="formFields">
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              minHeight: 900,
              transition: 'background-color 0.2s ease',
              backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
              border: '2px dashed',
              borderColor: snapshot.isDraggingOver ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 2,
              position: 'relative',
              overflow: showPdfBackground ? 'hidden' : 'visible',
            }}
          >
            {/* PDF Arka Plan */}
            {showPdfBackground && backgroundPdf && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 0,
                  pointerEvents: 'none',
                  opacity: 0.7,
                }}
              >
                <iframe
                  src={backgroundPdf.url}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title="PDF Background"
                />
              </Box>
            )}

            <Grid 
              container 
              spacing={2}
              sx={{ 
                position: 'relative', 
                zIndex: showPdfBackground ? 1 : 'auto' 
              }}
            >
              {formFields.map((field, index) => (
                <Draggable
                  key={field.id.toString()}
                  draggableId={field.id.toString()}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <Grid
                      item
                      xs={12}
                      sm={field.responsive?.sm || 12}
                      md={field.responsive?.md || 12}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      sx={{
                        opacity: snapshot.isDragging ? 0.8 : 1,
                        position: showPdfBackground && field.pdfPosition ? 'absolute' : 'relative',
                        left: showPdfBackground && field.pdfPosition ? `${field.pdfPosition.x}%` : 'auto',
                        top: showPdfBackground && field.pdfPosition ? `${field.pdfPosition.y}%` : 'auto',
                        width: showPdfBackground && field.pdfPosition ? `${field.pdfPosition.width}%` : 'auto',
                        height: showPdfBackground && field.pdfPosition ? `${field.pdfPosition.height}%` : 'auto',
                        zIndex: showPdfBackground ? 2 : 'auto',
                      }}
                      onClick={() => handleFieldClick(field)}
                    >
                      <FormField
                        field={field}
                        onRemove={onRemoveField}
                        onUpdate={onUpdateField}
                        onDuplicate={onDuplicateField}
                        dragHandleProps={provided.dragHandleProps}
                        calculationValue={calculationValues[field.id]}
                        onCalculationChange={onCalculationChange}
                        showPdfPositioning={showPdfBackground}
                        onPositionUpdate={onFieldPositionUpdate}
                      />
                    </Grid>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Grid>
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};

const TableFieldEditor = ({ 
  field, 
  onUpdateField,
  onAddTableColumn,
  onUpdateTableColumn,
  onRemoveTableColumn,
  onAddSelectOption,
  onUpdateSelectOption,
  onRemoveSelectOption 
}) => {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Tablo Sütunları
      </Typography>
      
      {field.columns?.map((column, columnIndex) => (
        <Box
          key={columnIndex}
          sx={{
            p: 2,
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TextField
              size="small"
              label="Sütun Başlığı"
              value={column.header}
              onChange={(e) => onUpdateTableColumn(field.id, columnIndex, { header: e.target.value })}
              sx={{ mr: 2, flexGrow: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 120, mr: 2 }}>
              <InputLabel>Tip</InputLabel>
              <Select
                value={column.type}
                label="Tip"
                onChange={(e) => onUpdateTableColumn(field.id, columnIndex, { type: e.target.value })}
              >
                <MenuItem value="text">Metin</MenuItem>
                <MenuItem value="number">Sayı</MenuItem>
                <MenuItem value="date">Tarih</MenuItem>
                <MenuItem value="select">Seçim</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Genişlik"
              value={column.width}
              onChange={(e) => onUpdateTableColumn(field.id, columnIndex, { width: e.target.value })}
              sx={{ width: 100, mr: 2 }}
            />
            <IconButton 
              color="error" 
              onClick={() => onRemoveTableColumn(field.id, columnIndex)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>

          {column.type === 'select' && (
            <Box sx={{ ml: 2 }}>
              <Typography variant="caption" gutterBottom>
                Seçenekler
              </Typography>
              {column.options?.map((option, optionIndex) => (
                <Box key={optionIndex} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    size="small"
                    value={option}
                    onChange={(e) => onUpdateSelectOption(field.id, columnIndex, optionIndex, e.target.value)}
                    sx={{ mr: 1, flexGrow: 1 }}
                  />
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => onRemoveSelectOption(field.id, columnIndex, optionIndex)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => onAddSelectOption(field.id, columnIndex)}
                sx={{ mt: 1 }}
              >
                Seçenek Ekle
              </Button>
            </Box>
          )}
        </Box>
      ))}
      
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => onAddTableColumn(field.id)}
        sx={{ mt: 2 }}
      >
        Sütun Ekle
      </Button>
    </Box>
  );
};

export default FormArea;